import type { CSSProperties, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Button,
  DashedDivider,
  GrainOverlay,
  Logo,
  buttonVariants,
  cn,
} from '@bill-alps/ui';
import { LanguageSwitcher } from './language-switcher';

const mountainLineUrl = '/mountain-peak.svg';

export type ErrorAction = {
  label: string;
  href?: string;
  onClick?: () => void;
};

export type ErrorHelpLink = {
  href: string;
  label: string;
  description: string;
  icon: ReactNode;
};

export type ErrorIncident = {
  title: string;
  description: string;
  referenceLabel: string;
  reference: string;
  timeLabel: string;
  time: string;
};

type ErrorStateProps = {
  code: '404' | '500';
  accent: 'blue' | 'amber';
  eyebrow: string;
  title: string;
  description: string;
  primaryAction: ErrorAction;
  secondaryAction: ErrorAction;
  helpTitle: string;
  helpLinks: ErrorHelpLink[];
  incident?: ErrorIncident;
  variant?: 'in-layout' | 'standalone';
};

function ErrorActionControl({
  action,
  primary,
  accent,
}: {
  action: ErrorAction;
  primary: boolean;
  accent: ErrorStateProps['accent'];
}) {
  const variant = primary
    ? accent === 'blue'
      ? 'contained-blue'
      : 'contained-amber'
    : 'outline';
  const className = 'w-full sm:w-auto';

  if (action.href) {
    return (
      <a
        href={action.href}
        className={buttonVariants({ variant, size: 'lg', className })}
      >
        {action.label}
      </a>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size="lg"
      className={className}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );
}

function ErrorPanel({
  code,
  accent,
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
  helpTitle,
  helpLinks,
  incident,
  variant,
}: ErrorStateProps) {
  const accentColor =
    accent === 'blue' ? 'var(--color-riso-blue)' : 'var(--color-riso-amber)';
  const style = {
    '--error-accent': accentColor,
  } as CSSProperties;

  return (
    <section
      data-error-state
      className={cn(
        'relative flex w-full items-center justify-center overflow-hidden px-4',
        variant === 'standalone'
          ? 'flex-1 pb-16 pt-28 sm:px-6 sm:pb-20 sm:pt-32'
          : 'min-h-[60vh] py-10 sm:px-6 sm:py-16',
      )}
      style={style}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at top, rgb(29 95 167 / 0.10), transparent 36%), linear-gradient(180deg, rgb(255 255 255 / 0.6), transparent)',
        }}
      />
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute left-1/2 aspect-[1000/409.9] w-[min(1150px,130%)] -translate-x-1/2 bg-[var(--error-accent)] opacity-10',
          variant === 'standalone' ? 'top-20' : 'top-6',
        )}
        style={{
          WebkitMask: `url(${mountainLineUrl}) no-repeat center / contain`,
          mask: `url(${mountainLineUrl}) no-repeat center / contain`,
        }}
      />

      <div className="relative mx-auto w-full max-w-3xl text-center">
        <span className="inline-flex items-center rounded-full border border-[color-mix(in_srgb,var(--error-accent)_18%,transparent)] bg-white/80 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--error-accent)] shadow-hard-sm backdrop-blur-lg">
          {eyebrow}
        </span>
        <p className="mt-7 select-none font-display text-[clamp(6rem,18vw,7rem)] font-extrabold leading-none tracking-[-0.06em] text-[var(--error-accent)]">
          {code}
        </p>
        <h1 className="mt-4 text-balance font-display text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl">
          {title}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
          {description}
        </p>

        <div className="mt-7 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          <ErrorActionControl action={primaryAction} primary accent={accent} />
          <ErrorActionControl
            action={secondaryAction}
            primary={false}
            accent={accent}
          />
        </div>

        {incident && (
          <div className="mt-10 rounded-3xl border border-riso-amber/20 bg-riso-amber-light/90 p-5 text-left shadow-hard-sm sm:p-6">
            <div className="mb-3 flex items-center gap-2.5 text-riso-amber">
              <AlertTriangle className="size-[18px]" aria-hidden="true" />
              <p className="font-display text-sm font-bold uppercase tracking-[0.14em]">
                {incident.title}
              </p>
            </div>
            <p className="text-sm leading-relaxed text-foreground/85">
              {incident.description}
            </p>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {incident.referenceLabel}
                </dt>
                <dd className="mt-1 break-all font-mono text-sm font-semibold text-foreground">
                  {incident.reference}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {incident.timeLabel}
                </dt>
                <dd className="mt-1 text-sm font-semibold text-foreground">
                  {incident.time}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <div className={cn('text-left', incident ? 'mt-10' : 'mt-14')}>
          <DashedDivider className="opacity-55" />
          <p className="mb-4 mt-8 text-center font-display text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {helpTitle}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {helpLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="group flex items-start gap-3.5 rounded-3xl border border-border/80 bg-white/85 p-5 text-left text-inherit no-underline shadow-hard-sm transition-all duration-200 ease-spring hover:-translate-y-1 hover:border-[color-mix(in_srgb,var(--error-accent)_28%,var(--color-border))] hover:shadow-hard-lg focus-visible:-translate-y-1 focus-visible:border-[var(--error-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--error-accent)] focus-visible:ring-offset-2 motion-reduce:transform-none motion-reduce:transition-none"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-[var(--error-accent)] [&_svg]:size-5">
                  {link.icon}
                </span>
                <span>
                  <span className="block font-semibold text-foreground transition-colors group-hover:text-[var(--error-accent)]">
                    {link.label}
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                    {link.description}
                  </span>
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ErrorState(props: ErrorStateProps) {
  if (props.variant !== 'standalone') {
    return <ErrorPanel {...props} variant="in-layout" />;
  }

  return (
    <div className="relative flex min-h-[var(--app-viewport-svh)] flex-col overflow-x-hidden bg-background">
      <header className="absolute inset-x-0 top-0 z-10 mx-auto flex h-20 w-full max-w-6xl items-center justify-between px-5 sm:px-6">
        <a href="/dashboard" aria-label="Bill Alps">
          <Logo />
        </a>
        <LanguageSwitcher />
      </header>
      <ErrorPanel {...props} variant="standalone" />
      <footer className="relative z-[1] px-6 pb-8 text-center text-xs text-muted-foreground">
        Bill Alps · WAP Sàrl · Fribourg
      </footer>
      <GrainOverlay opacity={0.14} />
    </div>
  );
}
